/**
 * IFRAP-AI.fr - Main Application
 * Poste de commandement numérique de la Fondation IFRAP
 * 
 * Architecture:
 * - Public Layout: Site institutionnel (République Numérique style)
 * - Admin Layout: Back-office cockpit (Apollo 11 style)
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import RequireAuth from './components/RequireAuth';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import VeillePage from './pages/public/VeillePage';
import ComparisonPage from './pages/public/ComparisonPage';
import TVCountersPage from './pages/public/TVCountersPage';
import InfographicsPage from './pages/public/InfographicsPage';
import PublicationsPage from './pages/public/PublicationsPage';
import PublicationDetailPage from './pages/public/PublicationDetailPage';
import ThemePage from './pages/public/ThemePage';
import GammaRemotePage from './pages/public/GammaRemotePage';
import EmbedKpisPage from './pages/public/EmbedKpisPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import ConsolePage from './pages/admin/ConsolePage';
import KnowledgePage from './pages/admin/KnowledgePage';
import KPIsPage from './pages/admin/KPIsPage';
import OSINTPage from './pages/admin/OSINTPage';
import CredentialsPage from './pages/admin/CredentialsPage';
import DocumentationPage from './pages/admin/DocumentationPage';
import AlertsPage from './pages/admin/AlertsPage';
import SummaryPage from './pages/admin/SummaryPage';
import CostsPage from './pages/admin/CostsPage';
import DistributionPage from './pages/admin/DistributionPage';
import ChroniquePage from './pages/admin/ChroniquePage';
import CyberAgnesArchivesPage from './pages/admin/CyberAgnesArchivesPage';
import DataRequestPage from './pages/admin/DataRequestPage';
import ScraperPage from './pages/admin/ScraperPage';
import UniversalScraperPage from './pages/admin/UniversalScraperPage';
import HistoryPage from './pages/admin/HistoryPage';
import NewspaperCredentialsPage from './pages/admin/NewspaperCredentialsPage';
import ReportGeneratorPage from './pages/admin/ReportGeneratorPage';

// Placeholder pages for Phase 1b modules
const PlaceholderPage = ({ title, phase }) => (
  <div className="flex flex-col items-center justify-center h-full text-center">
    <div className="w-16 h-16 bg-[#FFB000]/20 rounded-full flex items-center justify-center mb-4">
      <svg className="w-8 h-8 text-[#FFB000]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <h2 className="text-xl font-mono text-[#FFB000] mb-2">{title}</h2>
    <p className="text-gray-500">Ce module sera disponible en Phase {phase}</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Pages SANS authentification */}
          <Route path="login" element={<LoginPage />} />
          <Route path="embed/kpis" element={<EmbedKpisPage />} />
          <Route path="gamma-remote" element={<GammaRemotePage />} />

          {/* TOUTES les autres pages nécessitent une connexion */}
          
          {/* Public Routes (protégées) */}
          <Route element={<RequireAuth><PublicLayout /></RequireAuth>}>
            <Route index element={<HomePage />} />
            <Route path="veille" element={<VeillePage />} />
            <Route path="comparaison" element={<ComparisonPage />} />
            <Route path="infographies" element={<InfographicsPage />} />
            <Route path="etudes" element={<PlaceholderPage title="Études IFRAP" phase="1b" />} />
            <Route path="kpis" element={<PlaceholderPage title="Indicateurs Économiques" phase="1b" />} />
            <Route path="about" element={<PlaceholderPage title="À propos" phase="1b" />} />
            <Route path="theme/:theme" element={<ThemePage />} />
          </Route>
          
          {/* Standalone pages (protégées) */}
          <Route path="compteurs" element={<RequireAuth><TVCountersPage /></RequireAuth>} />
          <Route path="publications" element={<RequireAuth><PublicationsPage /></RequireAuth>} />
          <Route path="publications/:id" element={<RequireAuth><PublicationDetailPage /></RequireAuth>} />
          
          {/* Admin Routes (protégées) */}
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="console" element={<ConsolePage />} />
            <Route path="documents" element={<KnowledgePage />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="kpis" element={<KPIsPage />} />
            <Route path="osint" element={<OSINTPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="distribution" element={<DistributionPage />} />
            <Route path="summary" element={<SummaryPage />} />
            <Route path="costs" element={<CostsPage />} />
            <Route path="chronique" element={<ChroniquePage />} />
            <Route path="cyberagnes-archives" element={<CyberAgnesArchivesPage />} />
            <Route path="data-request" element={<DataRequestPage />} />
            <Route path="scraper" element={<ScraperPage />} />
            <Route path="universal-scraper" element={<UniversalScraperPage />} />
            <Route path="config" element={<CredentialsPage />} />
            <Route path="credentials" element={<CredentialsPage />} />
            <Route path="documentation" element={<DocumentationPage />} />
            <Route path="history" element={<HistoryPage />} />
            <Route path="newspapers" element={<NewspaperCredentialsPage />} />
            <Route path="reports" element={<ReportGeneratorPage />} />
          </Route>
          
          {/* Direct theme shortcuts (protégés) */}
          <Route path="sante" element={<Navigate to="/theme/sante" replace />} />
          <Route path="retraites" element={<Navigate to="/theme/retraites" replace />} />
          <Route path="education" element={<Navigate to="/theme/education" replace />} />
          <Route path="energie" element={<Navigate to="/theme/energie" replace />} />
          <Route path="collectivites" element={<Navigate to="/theme/collectivites" replace />} />
          <Route path="emploi" element={<Navigate to="/theme/emploi" replace />} />
          <Route path="finances" element={<Navigate to="/theme/finances" replace />} />

          {/* Catch-all → login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
